import { useState, useEffect, lazy, Suspense } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, FileText, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

// Lazy load do formulário pesado
const TransportadoraForm = lazy(() => import("./TransportadoraForm"));

const ITEMS_PER_PAGE = 20;

export default function Transportadoras() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAnotacoesModal, setShowAnotacoesModal] = useState(false);
  const [selectedTransportadora, setSelectedTransportadora] = useState<any>(null);

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: listData, refetch, isLoading } = trpc.transportadoras.list.useQuery({
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    search: debouncedSearch || undefined,
  });
  const transportadoras = listData?.data || [];
  const totalPages = listData?.totalPages || 1;
  const totalCount = listData?.total || 0;

  // Mutation para deletar
  const deleteMutation = trpc.transportadoras.delete.useMutation({
    onSuccess: () => {
      toast.success("Transportadora deletada com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao deletar transportadora");
    },
  });

  // Mutation para atualizar anotações
  const updateMutation = trpc.transportadoras.update.useMutation({
    onSuccess: () => {
      toast.success("Anotações salvas com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao salvar anotações");
    },
  });

  const handleEdit = (transp: any) => {
    setEditingId(transp.id);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Tem certeza que deseja deletar esta transportadora?")) {
      deleteMutation.mutate({ id });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleFormSuccess = () => {
    refetch();
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Transportadoras</h1>
      </div>

      {/* Botões de ação */}
      <div className="flex gap-4">
        <Button onClick={() => { setEditingId(null); setShowForm(true); }} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Novo
        </Button>
        <Button variant="outline" onClick={() => toast.info("Relatório em desenvolvimento")}>
          <FileText className="w-4 h-4 mr-2" />
          Relatório
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filtros:</span>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por razão social, fantasia, CNPJ ou cidade..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">COD.</TableHead>
                <TableHead>Razão Social</TableHead>
                <TableHead>Fantasia</TableHead>
                <TableHead className="w-24">Anotações</TableHead>
                <TableHead>CPF/CNPJ</TableHead>
                <TableHead>Grupo Econômico</TableHead>
                <TableHead>Cidade</TableHead>
                <TableHead className="w-12">UF</TableHead>
                <TableHead className="w-16">TAG</TableHead>
                <TableHead className="w-24">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transportadoras.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    Nenhuma transportadora encontrada
                  </TableCell>
                </TableRow>
              ) : (
                transportadoras.map((transp: any) => (
                  <TableRow key={transp.id}>
                    <TableCell className="font-medium">#{transp.id}</TableCell>
                    <TableCell>{transp.razaoSocial}</TableCell>
                    <TableCell>{transp.nomeFantasia || "-"}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTransportadora(transp);
                          setShowAnotacoesModal(true);
                        }}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </TableCell>
                    <TableCell>{transp.cnpj || "-"}</TableCell>
                    <TableCell>{transp.grupoEconomico || "-"}</TableCell>
                    <TableCell>{transp.cidade || "-"}</TableCell>
                    <TableCell>{transp.uf || "-"}</TableCell>
                    <TableCell>{transp.tags || "-"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(transp)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(transp.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} de {totalCount} registros
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
            {totalPages <= 5 ? (
              Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))
            ) : (
              <>
                <Button
                  variant={currentPage === 1 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </Button>
                {currentPage > 3 && <span className="px-2">...</span>}
                {currentPage > 2 && currentPage < totalPages - 1 && (
                  <Button variant="default" size="sm">
                    {currentPage}
                  </Button>
                )}
                {currentPage < totalPages - 2 && <span className="px-2">...</span>}
                <Button
                  variant={currentPage === totalPages ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Próximo
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal de Formulário - Lazy Loaded */}
      {showForm && (
        <Suspense fallback={
          <Dialog open={true}>
            <DialogContent className="!max-w-[95vw] !w-full max-h-[95vh]">
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </DialogContent>
          </Dialog>
        }>
          <TransportadoraForm
            open={showForm}
            onClose={handleFormClose}
            editingId={editingId}
            onSuccess={handleFormSuccess}
          />
        </Suspense>
      )}

      {/* Modal de Anotações */}
      <Dialog open={showAnotacoesModal} onOpenChange={setShowAnotacoesModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anotações - {selectedTransportadora?.razaoSocial}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={selectedTransportadora?.anotacoes || ""}
              onChange={(e) => setSelectedTransportadora({ ...selectedTransportadora, anotacoes: e.target.value })}
              rows={6}
              placeholder="Digite suas anotações..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAnotacoesModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (selectedTransportadora) {
                    updateMutation.mutate({
                      id: selectedTransportadora.id,
                      anotacoes: selectedTransportadora.anotacoes,
                    });
                    setShowAnotacoesModal(false);
                  }
                }}
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
